import * as React from "react";

import {
  AccessListingPage,
  AccessOverviewPage,
  AccessRadiusSearchPage,
  AccessReportsCoverPageElementEnum,
  AccessReportsWizardDetailsPage,
  AccessReportsWizardElement,
  AccessReportsWizardElementTypeEnum,
  AccessReportsWizardEmployeeCountPage,
  AccessReportsWizardFormGroup,
  AccessReportsWizardGroupPropertyIdEnum,
  AccessReportsWizardOption,
  AccessReportsWizardProviderCountPage,
  AccessReportsWizardStepEnum,
  AccessReportsWizardSummaryPage,
} from "@app/access-reports/shared";
import { AccessReportParametersModel } from "@app/access-reports/wizard";
import { LoadingSpinner } from "@app/shared";
import { QESButton, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";

interface AccessReportsWizardFormProps {
  accessReportParameters: AccessReportParametersModel;
  formGroups: AccessReportsWizardFormGroup[];
  getDisabledStatus: (formGroup: AccessReportsWizardFormGroup, element: AccessReportsWizardElement, byProductDefaultId?: number) => boolean;
  invalidElements: Map<string, string>;
  invalidPageOptionPageElements: Map<string, string>;
  onAddPageClick: (groupProperty: AccessReportsWizardGroupPropertyIdEnum) => void;
  onCheckboxChange: (wizardStep: AccessReportsWizardStepEnum, formGroupId: number, key: keyof AccessReportParametersModel, byProductDefaultId?: number) => void;
  onInputChange: (wizardStep: AccessReportsWizardStepEnum, formGroupId: number, key: keyof AccessReportParametersModel, byProductDefaultId?: number, event?: React.ChangeEvent<HTMLInputElement>, currentValue?: string,) => void;
  onRemovePageClick: (groupProperty: AccessReportsWizardGroupPropertyIdEnum, byProductDefaultId: number) => void;
  onSelectChange: (wizardStep: AccessReportsWizardStepEnum, formGroupId: number, key: keyof AccessReportParametersModel, event: React.ChangeEvent<HTMLSelectElement>, byProductDefaultId?: number) => void;
  productId?: number;
  repeatedByProductDefaultIds: number[];
  unrequiredValidationElements: string[];
  wizardStep: AccessReportsWizardStepEnum;
}

class AccessReportsWizardForm extends React.Component<AccessReportsWizardFormProps> {
  constructor(props: AccessReportsWizardFormProps) {
    super(props);
  }

  public render() {
    return (
      <React.Fragment>
        {this.props.formGroups ?
          this.props.formGroups.map((formGroup, index) => (
            <React.Fragment key={index}>
              {this.shouldBuildFormGroup(formGroup) && this.buildFormGroup(formGroup)}
            </React.Fragment>
          )) :
          <div className="row">
            <div className="col-12">
              <div className="text-center lead mb-3">
                <LoadingSpinner /> Loading...
              </div>
            </div>
          </div>
        }
      </React.Fragment>
    );
  }

  private buildFormGroup = (formGroup: AccessReportsWizardFormGroup): React.ReactNode => {
    let isVisible = false;
    if (!formGroup.hidden) {
      // if a form group is flat-out marked as hidden, do not display it
      // this takes precedence over anything else
      if (!formGroup.groupPropertyId) {
        isVisible = true;
      } else {
        if (!!this.props.accessReportParameters && !!this.props.accessReportParameters.product) {
          if (!formGroup.hiddenProducts?.includes(this.props.accessReportParameters.product.id)) {
            isVisible = true;
          }
        }
      }
    }

    if (isVisible) {
      if (!!formGroup.groupPropertyId) {
        const pages = this.props.accessReportParameters[formGroup.groupPropertyId];

        return (
          <React.Fragment>
            {pages.map((page, index) => {
              let isRepeated = false;

              if (this.props.repeatedByProductDefaultIds?.includes(page.id)) {
                isRepeated = true;
              }

              return (
                <div className="row align-items-center" key={index}>
                  {(!!formGroup.elements && !isRepeated) &&
                    this.buildFormElement(formGroup.elements[0], formGroup, 0, isRepeated, page)
                  }
                  <div className={`col-9 ${isRepeated ? "offset-md-2" : ""}`}>
                    <div className="row">
                      {formGroup.elements?.filter(x => !x.hidden).map((element, index) => {
                        if (index !== 0) {
                          return this.buildFormElement(element, formGroup, index, isRepeated, page)
                        }
                      })}
                    </div>
                  </div>
                  {isRepeated &&
                    <div className="col-1">
                      <QESButton
                        qesTestId={`access-reports-wizard__form--remove-form-group-${formGroup.id}`}
                        qesType="secondary-danger"
                        className="mr-3"
                        onClick={() => this.props.onRemovePageClick(formGroup.groupPropertyId, page.id)}
                      >
                        Remove
                      </QESButton>
                    </div>
                  }
                </div>
              );
            })}

            {(!formGroup.disallowedMultipleProducts?.includes(this.props.productId) && !formGroup.hiddenProducts?.includes(this.props.productId)) && (
              <div className="row">
                <div className="offset-md-2 col-md-10">
                  <QESButton
                    className="p-0"
                    qesTestId={`access-reports-wizard__form--add-another-form-group-${formGroup.id}`}
                    qesType="secondary"
                    onClick={() => this.props.onAddPageClick(formGroup.groupPropertyId)}
                  >
                    <i className="fa fa-plus" /> Add another
                  </QESButton>
                </div>
              </div>
            )}
            <hr />
          </React.Fragment>
        );
      } else {
        return (
          <React.Fragment>
            {formGroup.title &&
              <div className="row">
                <div className="col-12">
                  <label className="label-m mb-2">
                    {formGroup.title}
                  </label>
                </div>
              </div>
            }

            <div className="row">
              {formGroup.elements?.filter(x => !x.hidden).map((element, index) => {
                return this.buildFormElement(element, formGroup, index, false)
              })}
            </div>
          </React.Fragment>
        );
      }
    }
  }

  private buildFormElement = (element: AccessReportsWizardElement, formGroup: AccessReportsWizardFormGroup, index: number, isRepeatedFormGroup: boolean, page?: AccessListingPage | AccessOverviewPage | AccessRadiusSearchPage | AccessReportsWizardSummaryPage | AccessReportsWizardDetailsPage | AccessReportsWizardEmployeeCountPage | AccessReportsWizardProviderCountPage): React.ReactNode => {
    let isHiddenByProduct = false;

    if (!!this.props.accessReportParameters && !!this.props.accessReportParameters.product) {
      const productOption: AccessReportsWizardOption = !!element.options && element.options.find(x => x.id === this.props.accessReportParameters.product.id);

      if (!!productOption && !!productOption.hiddenProducts && productOption.hiddenProducts.includes(this.props.accessReportParameters.product.id)) {
        isHiddenByProduct = true;
      }
    }

    if (!isHiddenByProduct) {
      const disabled: boolean = this.props.getDisabledStatus(formGroup, element, page?.id);
      const uniquePageIdSuffix = !!page ? `-${page.id}` : "";
      const value = this.getElementValue(element, formGroup, page);
      let elementNode: React.ReactNode = null;

      switch (element.elementType) {
        case AccessReportsWizardElementTypeEnum.Checkbox:
          elementNode = (
            <div className="d-flex align-items-center w-100 h-100">
              <QESCheckbox
                checked={value}
                disabled={disabled}
                label={element.title}
                testId={`access-reports-wizard__form--${element.elementId}-checkbox${uniquePageIdSuffix}`}
                onChange={() => this.props.onCheckboxChange(this.props.wizardStep, formGroup.id, element.elementId as keyof AccessReportParametersModel, page?.id)}
              />
            </div>
          );

          break;
        case AccessReportsWizardElementTypeEnum.Dropdown:
          let options: AccessReportsWizardOption[] = [];
          if (!!element.previousRequiredElementId && element.usePreviousElementFilterId) {
            const previousRequiredElement: AccessReportsWizardElement = formGroup.elements.find((x) => x.elementId === element.previousRequiredElementId);

            if (!!previousRequiredElement) {
              const previousRequiredElementValue = this.getElementValue(previousRequiredElement, formGroup, page);

              if (previousRequiredElementValue !== null && previousRequiredElementValue !== undefined) {
                options = element.options.filter((x) => ((x.previousElementFilterId !== null && x.previousElementFilterId !== undefined) && (x.previousElementFilterId.toString() === previousRequiredElementValue.toString())));
              }
            }
          } else {
            options = element.options;
          }

          if (!!this.props.accessReportParameters && !!this.props.accessReportParameters.product) {
            options = options.filter((x) => (!!x.hiddenProducts && !x.hiddenProducts.includes(this.props.accessReportParameters.product.id)));
          }

          elementNode = (
            <QESSelect
              allowEmpty={true}
              className="mb-3"
              disabled={disabled}
              isValid={!element.required || element.required && !!value}
              testId={`access-reports-wizard__form--${element.elementId}-dropdown${uniquePageIdSuffix}`}
              label={element.title}
              required={element.required && !disabled}
              onChange={(e) => this.props.onSelectChange(this.props.wizardStep, formGroup.id, element.elementId as keyof AccessReportParametersModel, e, page?.id)}
              value={value}
            >
              {options && options.map((option, optionIndex) => (
                <option
                  key={optionIndex}
                  value={option.id}
                >
                  {option.displayName ? option.displayName : option.name}
                </option>
              ))}
            </QESSelect>
          );

          break;
        case AccessReportsWizardElementTypeEnum.Input:
          elementNode = (
            <QESTextInput
              className="mb-3"
              testId={`access-reports-wizard__form--${element.elementId}-input${uniquePageIdSuffix}`}
              disabled={disabled}
              errorText={!!formGroup.groupPropertyId ? this.props.invalidPageOptionPageElements.get(page?.id + element.elementId) : this.props.invalidElements.get(element.elementId)}
              isValid={!!formGroup.groupPropertyId ? !this.props.invalidPageOptionPageElements.get(page?.id + element.elementId) : !this.props.invalidElements.get(element.elementId)}
              label={element.title || " ".replace(/ /g, "\u00a0")}
              onChange={(e) => this.props.onInputChange(this.props.wizardStep, formGroup.id, element.elementId as keyof AccessReportParametersModel, page?.id, e)}
              onBlur={() => this.props.onInputChange(this.props.wizardStep, formGroup.id, element.elementId as keyof AccessReportParametersModel, page?.id, null, value?.toString())}
              required={element.required}
              unrequiredValidation={this.props.unrequiredValidationElements.includes(element.elementId)}
              value={value}
            />
          );

          break;
        default:
          console.error(`Invalid Access Reports Wizard Element Type: ${element.elementType}`);
      }

      return (
        <div
          className={`col-${(!!formGroup.groupPropertyId && !isRepeatedFormGroup && index === 0) ? "2" : (!!formGroup.groupPropertyId) ? "4" : "3"}`}
          key={index}
        >
          {elementNode}
        </div>
      );
    }
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  private getElementValue = (element: AccessReportsWizardElement, formGroup: AccessReportsWizardFormGroup, page?: AccessListingPage | AccessOverviewPage | AccessRadiusSearchPage | AccessReportsWizardSummaryPage | AccessReportsWizardDetailsPage | AccessReportsWizardEmployeeCountPage | AccessReportsWizardProviderCountPage) => {
    if (formGroup.groupPropertyId) {
      const formGroupModels = (this.props.accessReportParameters as any)[formGroup.groupPropertyId];
      const formGroupModel = formGroupModels?.find((x: any) => x.id === page.id)

      if (!!formGroupModel) {
        if (element.elementType === AccessReportsWizardElementTypeEnum.Dropdown) {
          if (formGroupModel[element.elementId]) {
            return formGroupModel[element.elementId].id.toString();
          }
        } else {
          return formGroupModel[element.elementId];
        }
      }
    } else {
      if (element.elementType === AccessReportsWizardElementTypeEnum.Dropdown) {
        if ((this.props.accessReportParameters as any)[element.elementId]) {
          return (this.props.accessReportParameters as any)[element.elementId].id.toString();
        } else {
          return "";
        }

      } else {
        if (element.elementId === AccessReportsCoverPageElementEnum.To) {
          return this.props.accessReportParameters.accessSharedOutput?.to;
        } else {
          return (this.props.accessReportParameters as any)[element.elementId];
        }
      }
    }

    return "";
  }
  /* eslint-enable */

  private shouldBuildFormGroup = (formGroup: AccessReportsWizardFormGroup): boolean => {
    const hiddenElements: AccessReportsWizardElement[] = formGroup.elements.filter(x => x.hidden);

    if (formGroup.hidden || hiddenElements.length === formGroup.elements.length) {
      // if all the elements are hidden (or the form group itself is marked as hidden), do not display the form group
      return false;
    }

    return true;
  }
}

export { AccessReportsWizardForm };
